import { useState, useEffect } from 'react';
import { calendar, outlook } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, 
  ListTodo, FileText, Mic, Clock, Plus, ExternalLink,
  RefreshCw, CheckCircle2, XCircle, CloudOff, MapPin, Users
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const DAYS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Microsoft logo as SVG component
function MicrosoftLogo({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

function EventDot({ event }) {
  const getColor = () => {
    if (event.type === 'outlook') return 'bg-[#0078D4]';
    if (event.type === 'task') {
      if (event.priority === 'high') return 'bg-rose-500';
      if (event.priority === 'medium') return 'bg-amber-500';
      return 'bg-slate-400';
    }
    if (event.type === 'invoice') {
      return event.status === 'paid' ? 'bg-emerald-500' : 'bg-blue-500';
    }
    if (event.type === 'meeting') return 'bg-violet-500';
    return 'bg-gray-400';
  };
  
  return (
    <div 
      className={`w-2 h-2 rounded-full ${getColor()}`}
      title={event.title}
    />
  );
}

function DayCell({ date, events, isToday, isCurrentMonth, onSelectDate, isSelected }) {
  const dayEvents = events.filter(e => {
    const eventDate = new Date(e.date);
    return eventDate.getDate() === date.getDate() && 
           eventDate.getMonth() === date.getMonth() &&
           eventDate.getFullYear() === date.getFullYear();
  });
  
  return (
    <div 
      onClick={() => onSelectDate(date)}
      data-testid={`calendar-day-${date.getDate()}`}
      className={`min-h-[80px] p-1 border border-border/50 cursor-pointer transition-all hover:bg-accent/50 ${
        !isCurrentMonth ? 'bg-muted/30 text-muted-foreground' : ''
      } ${isToday ? 'bg-primary/10 border-primary' : ''} ${
        isSelected ? 'ring-2 ring-primary' : ''
      }`}
    >
      <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary font-bold' : ''}`}>
        {date.getDate()}
      </div>
      <div className="flex flex-wrap gap-0.5">
        {dayEvents.slice(0, 4).map((event, idx) => (
          <EventDot key={idx} event={event} />
        ))}
        {dayEvents.length > 4 && (
          <span className="text-xs text-muted-foreground">+{dayEvents.length - 4}</span>
        )}
      </div>
    </div>
  );
}

function EventListItem({ event, onOpenOutlook }) {
  const typeIcons = {
    task: ListTodo,
    invoice: FileText,
    meeting: Mic,
    outlook: CalendarIcon,
  };
  const Icon = typeIcons[event.type] || Clock;
  
  const typeLabels = {
    task: 'Tarea',
    invoice: 'Factura',
    meeting: 'Reunión',
    outlook: 'Outlook',
  };
  
  const statusLabels = {
    pending: 'Pendiente',
    in_progress: 'En Progreso',
    completed: 'Completada',
    paid: 'Pagada',
    sent: 'Enviada',
    draft: 'Borrador',
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };
  
  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-lg border hover:shadow-sm transition-all"
      data-testid={`event-item-${event.id}`}
    >
      <div 
        className="w-3 h-full min-h-[40px] rounded-full flex-shrink-0"
        style={{ backgroundColor: event.color }}
      />
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{event.title}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          {event.type === 'outlook' ? (
            <MicrosoftLogo className="h-3 w-3" />
          ) : (
            <Icon className="h-3 w-3" />
          )}
          <span>{typeLabels[event.type]}</span>
          
          {event.type === 'outlook' && event.start && (
            <span className="font-mono">
              {formatTime(event.start)}
              {event.end && ` - ${formatTime(event.end)}`}
            </span>
          )}
          
          {event.location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {event.location}
            </span>
          )}
          
          {event.status && (
            <Badge variant="outline" className="text-xs">
              {statusLabels[event.status] || event.status}
            </Badge>
          )}
        </div>
      </div>
      
      {event.amount && (
        <span className="text-sm font-mono font-medium">
          RD$ {event.amount.toLocaleString()}
        </span>
      )}
      
      {event.type === 'outlook' && event.web_link && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => window.open(event.web_link, '_blank')}
          title="Abrir en Outlook"
        >
          <ExternalLink className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

function CreateEventDialog({ open, onOpenChange, selectedDate, onEventCreated }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    start: '',
    end: '',
    body: '',
    location: '',
    attendees: '',
    is_all_day: false,
  });

  useEffect(() => {
    if (open && selectedDate) {
      const dateStr = selectedDate.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        start: `${dateStr}T09:00`,
        end: `${dateStr}T10:00`,
      }));
    }
  }, [open, selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.subject.trim()) {
      toast.error('El título es requerido');
      return;
    }

    setLoading(true);
    try {
      const attendeesArray = formData.attendees
        .split(',')
        .map(e => e.trim())
        .filter(e => e.length > 0);

      await outlook.createEvent({
        subject: formData.subject,
        start: formData.start,
        end: formData.end,
        body: formData.body || undefined,
        location: formData.location || undefined,
        attendees: attendeesArray.length > 0 ? attendeesArray : undefined,
        is_all_day: formData.is_all_day,
      });

      toast.success('Evento creado en Outlook');
      onOpenChange(false);
      onEventCreated();
      setFormData({
        subject: '',
        start: '',
        end: '',
        body: '',
        location: '',
        attendees: '',
        is_all_day: false,
      });
    } catch (err) {
      console.error('Error creating event:', err);
      toast.error(err.response?.data?.detail || 'Error al crear evento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MicrosoftLogo className="h-5 w-5" />
            Crear Evento en Outlook
          </DialogTitle>
          <DialogDescription>
            El evento se sincronizará automáticamente con tu calendario de Microsoft 365.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Título *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Reunión de equipo"
              required
              data-testid="event-subject-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start">Inicio</Label>
              <Input
                id="start"
                type="datetime-local"
                value={formData.start}
                onChange={(e) => setFormData(prev => ({ ...prev, start: e.target.value }))}
                data-testid="event-start-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end">Fin</Label>
              <Input
                id="end"
                type="datetime-local"
                value={formData.end}
                onChange={(e) => setFormData(prev => ({ ...prev, end: e.target.value }))}
                data-testid="event-end-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Ubicación</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              placeholder="Sala de conferencias / Teams"
              data-testid="event-location-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="attendees">Participantes (emails separados por coma)</Label>
            <Input
              id="attendees"
              value={formData.attendees}
              onChange={(e) => setFormData(prev => ({ ...prev, attendees: e.target.value }))}
              placeholder="persona1@empresa.com, persona2@empresa.com"
              data-testid="event-attendees-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Descripción</Label>
            <Textarea
              id="body"
              value={formData.body}
              onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
              placeholder="Agenda de la reunión..."
              rows={3}
              data-testid="event-body-input"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="create-event-submit">
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Evento
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outlookStatus, setOutlookStatus] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadEvents();
    checkOutlookStatus();
  }, [currentDate]);

  const checkOutlookStatus = async () => {
    try {
      const res = await outlook.getSyncStatus();
      setOutlookStatus(res.data);
    } catch (err) {
      console.error('Error checking Outlook status:', err);
      setOutlookStatus({ connected: false });
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      const res = await calendar.getEvents();
      setEvents(res.data);
    } catch (err) {
      console.error('Error loading events:', err);
      toast.error('Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await loadEvents();
      await checkOutlookStatus();
      toast.success('Calendario sincronizado');
    } catch (err) {
      toast.error('Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // Generate calendar days
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Previous month days
    const firstDayOfWeek = firstDay.getDay();
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  const calendarDays = generateCalendarDays();
  const today = new Date();

  // Get events for selected date
  const selectedDateEvents = events.filter(e => {
    const eventDate = new Date(e.date);
    return eventDate.toDateString() === selectedDate.toDateString();
  });

  // Get upcoming events (next 7 days)
  const upcomingEvents = events.filter(e => {
    const eventDate = new Date(e.date);
    const now = new Date();
    const weekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    return eventDate >= now && eventDate <= weekLater;
  }).sort((a, b) => new Date(a.date) - new Date(b.date)).slice(0, 10);

  // Count events by type
  const outlookEventsCount = events.filter(e => e.type === 'outlook').length;
  const localEventsCount = events.filter(e => e.type !== 'outlook').length;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="calendar-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Calendario</h1>
          <p className="text-muted-foreground">Vista de tareas, facturas, reuniones y eventos de Outlook</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Outlook Status Badge */}
          {outlookStatus && (
            <Badge 
              variant={outlookStatus.connected ? "default" : "secondary"}
              className="gap-1.5"
              data-testid="outlook-status-badge"
            >
              {outlookStatus.connected ? (
                <>
                  <MicrosoftLogo className="h-3 w-3" />
                  <span>{outlookStatus.user || 'Conectado'}</span>
                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                </>
              ) : (
                <>
                  <CloudOff className="h-3 w-3" />
                  <span>Outlook no conectado</span>
                </>
              )}
            </Badge>
          )}

          <Button 
            variant="outline" 
            size="icon"
            onClick={handleSync}
            disabled={syncing}
            title="Sincronizar"
            data-testid="sync-calendar-btn"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          </Button>

          <Button variant="outline" onClick={goToToday}>
            Hoy
          </Button>

          {outlookStatus?.connected && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-event-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Evento
            </Button>
          )}
        </div>
      </div>

      {/* Event count summary */}
      <div className="flex gap-4 text-sm text-muted-foreground">
        <span>{localEventsCount} eventos locales</span>
        {outlookStatus?.connected && (
          <span className="flex items-center gap-1">
            <MicrosoftLogo className="h-3 w-3" />
            {outlookEventsCount} eventos de Outlook
          </span>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Calendar Grid */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <CardTitle className="text-xl">
                {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Days Header */}
            <div className="grid grid-cols-7 gap-0 mb-1">
              {DAYS.map(day => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-7 gap-0">
                {calendarDays.map(({ date, isCurrentMonth }, idx) => (
                  <DayCell
                    key={idx}
                    date={date}
                    events={events}
                    isToday={date.toDateString() === today.toDateString()}
                    isCurrentMonth={isCurrentMonth}
                    isSelected={date.toDateString() === selectedDate.toDateString()}
                    onSelectDate={setSelectedDate}
                  />
                ))}
              </div>
            )}

            {/* Legend */}
            <div className="flex items-center gap-4 mt-4 pt-4 border-t flex-wrap">
              <div className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full bg-rose-500" />
                <span>Tarea Alta</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span>Tarea Media</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span>Factura</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full bg-violet-500" />
                <span>Reunión</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs">
                <div className="w-2 h-2 rounded-full bg-[#0078D4]" />
                <MicrosoftLogo className="h-3 w-3" />
                <span>Outlook</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Selected Date Events */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-primary" />
                {selectedDate.toLocaleDateString('es-ES', { 
                  weekday: 'long', 
                  day: 'numeric', 
                  month: 'long' 
                })}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDateEvents.length > 0 ? (
                <div className="space-y-2">
                  {selectedDateEvents.map((event, idx) => (
                    <EventListItem key={idx} event={event} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-2">No hay eventos para este día</p>
                  {outlookStatus?.connected && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowCreateDialog(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Crear evento
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-500" />
                Próximos 7 días
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-2">
                  {upcomingEvents.map((event, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <div 
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: event.color }}
                      />
                      <span className="text-muted-foreground text-xs w-16 flex-shrink-0">
                        {new Date(event.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                      </span>
                      <span className="flex-1 truncate">{event.title}</span>
                      {event.type === 'outlook' && (
                        <MicrosoftLogo className="h-3 w-3 flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">
                  No hay eventos próximos
                </p>
              )}
            </CardContent>
          </Card>

          {/* Quick Links */}
          <div className="grid grid-cols-2 gap-2">
            <Link to="/tasks">
              <Button variant="outline" className="w-full gap-2">
                <ListTodo className="h-4 w-4" />
                Tareas
              </Button>
            </Link>
            <Link to="/invoices">
              <Button variant="outline" className="w-full gap-2">
                <FileText className="h-4 w-4" />
                Facturas
              </Button>
            </Link>
          </div>

          {/* Outlook Connection Card */}
          {!outlookStatus?.connected && (
            <Card className="border-dashed">
              <CardContent className="pt-6">
                <div className="text-center space-y-3">
                  <MicrosoftLogo className="h-10 w-10 mx-auto" />
                  <div>
                    <h3 className="font-medium">Conecta tu Outlook</h3>
                    <p className="text-sm text-muted-foreground">
                      Sincroniza tus eventos de Microsoft 365
                    </p>
                  </div>
                  <Link to="/settings">
                    <Button variant="outline" className="w-full">
                      Ir a Configuración
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create Event Dialog */}
      <CreateEventDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        selectedDate={selectedDate}
        onEventCreated={loadEvents}
      />
    </div>
  );
}
